<script>
import accountInformation from "../components/settings/setting-accountInformation.vue";
import zapierConnections from "../components/settings/setting-zapierConnections.vue";
import zohoIntegration from "../components/settings/setting-zohoIntegration.vue";
import webhookManager from "../components/settings/setting-webhookManager.vue";
import apiInformation from "../components/settings/setting-apiInformation.vue";
import reviewScreenVersions from "../components/settings/setting-reviewScreenVersions.vue";
import styleEditor from "../components/settings/setting-styleEditor.vue";
import accountLabels from "../components/settings/setting-accountLabels";
import accountProperties from "../components/settings/setting-accountProperties";
import customerPortal from "../components/settings/setting-customerPortal.vue";
import smartReview from "../components/settings/setting-smartReview.vue";
import watermarkImage from "../components/settings/setting-watermarkImage.vue";
import workflowDefaults from "../components/settings/setting-workflowDefaults.vue";
import defaultEmailTemplates from "../components/settings/setting-defaultEmailTemplates.vue";
import defaultAnnotationTools from "../components/settings/setting-defaultAnnotationTools.vue";
import emailBranding from "../components/settings/setting-emailBranding.vue";
import dangerZone from "../components/settings/setting-dangerZone.vue";
import { updateFaviconAndTitle, isWatermarkPlanEnabled } from "../utils/helpers"
import { PLAN_SKU_TYPES, PREMIUM_FEATURE_CONTENT, PREMIUM_FEATURES_KEYS, ROUTES } from '../utils/constants';

const { FREE_PLAN, SUSPENDED_PLAN, TRIAL_PLAN } = PLAN_SKU_TYPES

const SECTIONS = [
  {
    key: "organization",
    label: "Organization",
    icon: "building",
  },
  {
    key: "branding",
    label: "Branding",
    icon: "palette",
  },
  {
    key: "email-branding",
    label: "Email Branding",
    icon: "mail",
  },
  {
    key: "properties",
    label: "Properties",
    icon: "list",
  },
  {
    key: "portal",
    label: "Contact Portal",
    icon: "users",
  },
  {
    key: "preferences",
    label: "Preferences",
    icon: "sliders",
  },
  {
    key: "integrations",
    label: "Integrations",
    icon: "plug",
  },
  {
    key: "danger",
    label: "Danger",
    icon: "alert",
  },
];

export default {
  components: {
    accountInformation,
    accountProperties,
    customerPortal,
    smartReview,
    watermarkImage,
    workflowDefaults,
    defaultEmailTemplates,
    defaultAnnotationTools,
    emailBranding,
    zapierConnections,
    zohoIntegration,
    webhookManager,
    apiInformation,
    reviewScreenVersions,
    styleEditor,
    accountLabels,
    dangerZone,
  },
  data() {
    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem("experience-banner::account::dismissed") === "1";
    } catch (e) {
      // localStorage unavailable
    }
    const hash = (window.location.hash || "").replace("#", "");
    return {
      bannerDismissed: dismissed,
      sections: SECTIONS,
      activeSection: SECTIONS.some((s) => s.key === hash) ? hash : "organization",
      updatedAccountLogo: null,
      integrationsGateContent: PREMIUM_FEATURE_CONTENT[PREMIUM_FEATURES_KEYS.INTEGRATION] || {},
      portalGateContent: PREMIUM_FEATURE_CONTENT[PREMIUM_FEATURES_KEYS.PORTAL] || {},
      workflowsGateContent: PREMIUM_FEATURE_CONTENT[PREMIUM_FEATURES_KEYS.WORKFLOWS] || {},
    }
  },
  async mounted() {
    await this.$A.GetCurAccountData();
    // Non-admins deep-linking to #danger land on Organization instead
    if (this.activeSection === "danger" && !this.isAccountAdmin) {
      this.selectSection("organization");
    }
  },
  computed: {
    isFreeUser() {
      const currentSubscription = _.get(this.$A, 'Auth.account.subscriptionState', []);
      return currentSubscription.sku === FREE_PLAN || currentSubscription.sku === SUSPENDED_PLAN || currentSubscription.sku === TRIAL_PLAN;
    },
    // The contact portal is premium-tier and above (server enforces the same rule)
    isPortalPlanAllowed() {
      const sku = _.get(this.$A, 'Auth.account.subscriptionState.sku', '') || '';
      const tier = sku.split('-')[0];
      return tier === 'premium' || tier === 'enterprise';
    },
    // Custom watermark upload matches the watermarking feature gate (Standard/Premium).
    isWatermarkPlanAllowed() {
      return isWatermarkPlanEnabled(this.$A.Auth);
    },
    // Smart Review is a standard-tier-and-above feature (server enforces the same rule).
    // Note this excludes starter, so isFreeUser is not sufficient here.
    isSmartReviewPlanAllowed() {
      const sku = _.get(this.$A, 'Auth.account.subscriptionState.sku', '') || '';
      const tier = sku.split('-')[0];
      return tier === 'standard' || tier === 'premium' || tier === 'enterprise';
    },
    // Only admin-role users (account management permission, same check the
    // nav uses for the Account Settings link) may see the Danger section
    isAccountAdmin() {
      return _.get(this.$A, 'Auth.permissions.manageAccount', false) === true;
    },
    visibleSections() {
      return this.sections.filter((s) => s.key !== "danger" || this.isAccountAdmin);
    },
  },
  methods: {
    updateAccountLogo(logo) {
      updateFaviconAndTitle({ accountLogo: logo });

      this.updatedAccountLogo = logo;
    },
    selectSection(key) {
      this.activeSection = key;
      try {
        window.history.replaceState(null, "", `#${key}`);
      } catch (e) {
        window.location.hash = key;
      }
    },
    formatGateTitle(title) {
      if (!title) return '';
      return title.replace(/\{([^}]+)\}/g, '<span class="gate-highlight">$1</span>');
    },
    navigateToBilling() {
      this.$router.push(ROUTES.BILLING);
    },
    dismissBanner() {
      this.bannerDismissed = true;
      try {
        window.localStorage.setItem("experience-banner::account::dismissed", "1");
      } catch (e) {
        // localStorage unavailable
      }
    },
  }
};
</script>

<template>
  <div class="nav-margin account-page">
    <navigation :updatedAccountLogo="updatedAccountLogo"></navigation>

    <div class="page-body">
      <div class="experience-switch" v-if="!bannerDismissed">
        <div class="experience-msg">
          <span class="experience-pill">New</span>
          <span class="experience-text">You're using the new account settings.</span>
        </div>
        <div class="experience-actions">
          <button type="button" class="experience-link experience-link-btn" v-b-modal="'feedbackModal-newAccount'">Give feedback</button>
        </div>
        <button
          type="button"
          class="experience-close"
          @click="dismissBanner"
          aria-label="Dismiss banner"
          title="Dismiss"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <feedbackModal
        name="feedbackModal-newAccount"
        campaign-key="new-account-v1"
        title="How are the new account settings?"
        subtitle="Your take helps us decide what to improve."
        scale-question="How would you rate the new account settings experience?"
        :scale-min="1"
        :scale-max="7"
        scale-low-label="Not great"
        scale-high-label="Excellent"
        text-prompt="What's working or not working?"
        text-placeholder="Tell us anything that helps."
        submit-label="Send feedback"
      />

      <div class="account-shell">
        <aside class="settings-rail">
          <div class="rail-head">
            <div class="rail-head-title">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M3 21h18" />
                <path d="M5 21V7l8-4v18" />
                <path d="M19 21V11l-6-4" />
              </svg>
              <span>Account Settings</span>
            </div>
          </div>

          <nav class="rail-scroll" aria-label="Account settings sections">
            <button
              v-for="section in visibleSections"
              :key="section.key"
              type="button"
              class="rail-nav-item"
              :class="{ 'is-active': activeSection === section.key }"
              @click="selectSection(section.key)"
            >
              <svg v-if="section.icon === 'building'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 21h18" />
                <path d="M5 21V7l8-4v18" />
                <path d="M19 21V11l-6-4" />
              </svg>
              <svg v-else-if="section.icon === 'palette'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="13.5" cy="6.5" r=".5" />
                <circle cx="17.5" cy="10.5" r=".5" />
                <circle cx="8.5" cy="7.5" r=".5" />
                <circle cx="6.5" cy="12.5" r=".5" />
                <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
              </svg>
              <svg v-else-if="section.icon === 'mail'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              <svg v-else-if="section.icon === 'list'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
              <svg v-else-if="section.icon === 'sliders'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="4" y1="21" x2="4" y2="14" />
                <line x1="4" y1="10" x2="4" y2="3" />
                <line x1="12" y1="21" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12" y2="3" />
                <line x1="20" y1="21" x2="20" y2="16" />
                <line x1="20" y1="12" x2="20" y2="3" />
                <line x1="1" y1="14" x2="7" y2="14" />
                <line x1="9" y1="8" x2="15" y2="8" />
                <line x1="17" y1="16" x2="23" y2="16" />
              </svg>
              <svg v-else-if="section.icon === 'plug'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
              </svg>
              <svg v-else-if="section.icon === 'users'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span>{{ section.label }}</span>
            </button>
          </nav>
        </aside>

        <main class="account-main">
          <div class="account-main-inner">
            <div v-show="activeSection === 'organization'" class="section-stack">
              <accountInformation v-on:uploadAccountLogo="updateAccountLogo($event)"/>
            </div>
            <div v-show="activeSection === 'branding'" class="section-stack">
              <reviewScreenVersions />
              <styleEditor/>
              <accountLabels/>
            </div>
            <div v-show="activeSection === 'email-branding'" class="section-stack">
              <emailBranding />
            </div>
            <div v-show="activeSection === 'properties'" class="section-stack">
              <accountProperties/>
            </div>
            <div v-show="activeSection === 'portal'" class="section-stack">
              <div v-if="!isPortalPlanAllowed" class="settings-card gate-card">
                <div class="gate-body">
                  <span class="gate-badge">{{ portalGateContent.badge }}</span>
                  <h2 class="gate-title" v-html="formatGateTitle(portalGateContent.title)"></h2>
                  <p class="gate-description">{{ portalGateContent.description }}</p>
                  <ul class="gate-features">
                    <li v-for="(feature, idx) in portalGateContent.features" :key="idx">
                      <img src="/img/check-circle.svg" alt="" class="gate-check" />
                      {{ feature }}
                    </li>
                  </ul>
                </div>
                <div class="gate-foot">
                  <button type="button" class="primary-btn" @click="navigateToBilling">{{ portalGateContent.upgradeText }}</button>
                </div>
              </div>
              <customerPortal v-else />
            </div>
            <div v-show="activeSection === 'preferences'" class="section-stack">
              <!-- Workflow defaults require workflow access (paid plans) -->
              <div v-if="isFreeUser" class="settings-card gate-card">
                <div class="gate-body">
                  <span class="gate-badge">{{ workflowsGateContent.badge }}</span>
                  <h2 class="gate-title" v-html="formatGateTitle(workflowsGateContent.title)"></h2>
                  <p class="gate-description">{{ workflowsGateContent.description }}</p>
                  <ul class="gate-features">
                    <li v-for="(feature, idx) in workflowsGateContent.features" :key="idx">
                      <img src="/img/check-circle.svg" alt="" class="gate-check" />
                      {{ feature }}
                    </li>
                  </ul>
                </div>
                <div class="gate-foot">
                  <button type="button" class="primary-btn" @click="navigateToBilling">{{ workflowsGateContent.upgradeText }}</button>
                </div>
              </div>
              <workflowDefaults v-else />
              <defaultEmailTemplates />
              <defaultAnnotationTools />
              <!-- Custom watermark upload is gated to Standard and above; others see an upgrade prompt -->
              <div v-if="!isWatermarkPlanAllowed" class="settings-card gate-card">
                <div class="gate-body">
                  <span class="gate-badge">Standard</span>
                  <h2 class="gate-title">Add a custom watermark to your proofs</h2>
                  <p class="gate-description">
                    Stamp every page of your PDF proofs with your own image — a plain
                    “Proof” mark or any logo you choose — so the versions your approvers
                    see and screenshot carry exactly the mark you want.
                  </p>
                  <ul class="gate-features">
                    <li>
                      <img src="/img/check-circle.svg" alt="" class="gate-check" />
                      Upload a custom watermark image instead of your account logo
                    </li>
                    <li>
                      <img src="/img/check-circle.svg" alt="" class="gate-check" />
                      Applied automatically to every page as your files are processed
                    </li>
                    <li>
                      <img src="/img/check-circle.svg" alt="" class="gate-check" />
                      Falls back to your account logo whenever no custom image is set
                    </li>
                  </ul>
                  <p class="gate-description gate-availability">
                    Available on Standard plans and above.
                  </p>
                </div>
                <div class="gate-foot">
                  <button type="button" class="primary-btn" @click="navigateToBilling">Upgrade Plan</button>
                </div>
              </div>
              <watermarkImage v-else />
              <!-- Smart Review is gated to standard and above; starter/free see an upgrade prompt -->
              <div v-if="!isSmartReviewPlanAllowed" class="settings-card gate-card">
                <div class="gate-body">
                  <span class="gate-badge">Standard</span>
                  <h2 class="gate-title">Turn on Smart Review to get approvals faster</h2>
                  <p class="gate-description">
                    When you upload a new version of a proof, Smart Review automatically
                    compares it to the previous one and highlights exactly what changed —
                    so you and your approvers never have to hunt for edits by eye.
                  </p>
                  <ul class="gate-features">
                    <li>
                      <img src="/img/check-circle.svg" alt="" class="gate-check" />
                      Changed text, images, and moved elements marked right on the proof
                    </li>
                    <li>
                      <img src="/img/check-circle.svg" alt="" class="gate-check" />
                      A version-to-version list of every change — no manual side-by-side
                    </li>
                    <li>
                      <img src="/img/check-circle.svg" alt="" class="gate-check" />
                      Approvers see what's new at a glance and approve faster, with confidence
                    </li>
                    <li>
                      <img src="/img/check-circle.svg" alt="" class="gate-check" />
                      Catch unintended edits before they ever reach your client
                    </li>
                  </ul>
                  <p class="gate-description gate-availability">
                    Available on Standard plans and above.
                  </p>
                </div>
                <div class="gate-foot">
                  <button type="button" class="primary-btn" @click="navigateToBilling">Upgrade Plan</button>
                </div>
              </div>
              <smartReview v-else />
            </div>
            <div v-if="activeSection === 'integrations'" class="section-stack">
              <!-- Free/suspended/trial accounts see an inline upgrade panel instead of the integrations -->
              <div v-if="isFreeUser" class="settings-card gate-card">
                <div class="gate-body">
                  <span class="gate-badge">{{ integrationsGateContent.badge }}</span>
                  <h2 class="gate-title" v-html="formatGateTitle(integrationsGateContent.title)"></h2>
                  <p class="gate-description">{{ integrationsGateContent.description }}</p>
                  <ul class="gate-features">
                    <li v-for="(feature, idx) in integrationsGateContent.features" :key="idx">
                      <img src="/img/check-circle.svg" alt="" class="gate-check" />
                      {{ feature }}
                    </li>
                  </ul>
                </div>
                <div class="gate-foot">
                  <button type="button" class="primary-btn" @click="navigateToBilling">{{ integrationsGateContent.upgradeText }}</button>
                </div>
              </div>
              <template v-else>
                <apiInformation />
              <div class="settings-card" style="padding:24px">
                <h3>MCP connections</h3>
                <p>Connect Claude, Codex or Grok Bot to your proofs with access you choose.</p>
                <router-link to="/mcp">Set up and manage MCP connections →</router-link>
              </div>
              <zohoIntegration />
                <webhookManager />
                <zapierConnections />
              </template>
            </div>
            <div v-if="isAccountAdmin" v-show="activeSection === 'danger'" class="section-stack">
              <dangerZone />
            </div>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* The document scrolls naturally (promo banners etc. can add height
   above us); the rail stays pinned via position:sticky instead of a
   fixed-height shell, so there's a single page scrollbar. */
.page-body {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 64px); /* clear position:fixed nav */
}

/* Experience banner */
.experience-switch {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  column-gap: 16px;
  row-gap: 4px;
  padding: 8px 48px;
  background: rgba(1, 146, 86, 0.06);
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  font-size: 13px;
  color: #4b5563;
  flex-shrink: 0;
}
.experience-msg,
.experience-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.experience-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: #019256;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  letter-spacing: 0.02em;
}
.experience-link {
  color: #019256;
  font-weight: 600;
  text-decoration: none;
}
.experience-link:hover {
  text-decoration: underline;
  color: #017a48;
}
.experience-link-btn {
  background: transparent;
  border: 0;
  padding: 0;
  font: inherit;
  cursor: pointer;
}
.experience-close {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.experience-close:hover {
  background: rgba(15, 23, 42, 0.06);
  color: #111827;
}

/* Shell: sidebar + main column, like the proof list */
.account-shell {
  display: flex;
  align-items: stretch;
  flex: 1;
  background: #f4f5f7;
  position: relative;
}
.settings-rail {
  width: 288px;
  flex-shrink: 0;
  align-self: flex-start;
  position: sticky;
  top: 64px; /* below the fixed nav */
  height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  background: #fbfbfc;
  border-right: 1px solid rgba(15, 23, 42, 0.08);
  overflow: hidden;
}
.rail-head {
  flex-shrink: 0;
  height: 57px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 14px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
.rail-head-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.01em;
  white-space: nowrap;
}
.rail-head-title svg {
  color: #019256;
  flex-shrink: 0;
}
.rail-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px 10px 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  scrollbar-width: thin;
}
.rail-nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 12px;
  background: transparent;
  border: 0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #4b5563;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 120ms ease, color 120ms ease;
}
.rail-nav-item svg {
  flex-shrink: 0;
  color: #9ca3af;
  transition: color 120ms ease;
}
.rail-nav-item:hover {
  background: rgba(15, 23, 42, 0.04);
  color: #111827;
}
.rail-nav-item.is-active {
  background: rgba(1, 146, 86, 0.08);
  color: #017a48;
  font-weight: 600;
}
.rail-nav-item.is-active svg {
  color: #019256;
}

/* Main column — scrolls with the document */
.account-main {
  flex: 1;
  min-width: 0;
  background: #f4f5f7;
}
.account-main-inner {
  max-width: 991px;
  margin: 0 auto;
  padding: 24px;
}
.section-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
/* Inline upgrade panel shown to free/suspended/trial users on the
   Integrations section (replaces the old gated-features modal) */
.gate-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 8px 24px -16px rgba(8, 11, 13, 0.1);
  overflow: hidden;
}
.gate-body {
  padding: 32px;
}
.gate-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 12px;
  border-radius: 999px;
  background: rgba(1, 146, 86, 0.1);
  color: #017a48;
  font-size: 12px;
  font-weight: 600;
}
.gate-title {
  margin: 14px 0 0;
  font-size: 26px;
  line-height: 32px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #111827;
}
.gate-title ::v-deep .gate-highlight {
  color: #019256;
}
.gate-description {
  margin: 10px 0 0;
  font-size: 14px;
  color: #4b5563;
}
.gate-availability {
  margin-top: 16px;
  font-size: 13px;
  color: #6b7280;
}
.gate-features {
  margin: 18px 0 0;
  padding: 0;
  list-style: none;
}
.gate-features li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 5px 0;
  font-size: 13.5px;
  color: #4b5563;
}
.gate-check {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  margin-top: 1px;
}
.gate-foot {
  display: flex;
  justify-content: flex-end;
  padding: 12px 20px;
  background: #fbfbfc;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}
.primary-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 18px;
  background: #019256;
  color: #fff;
  border: 0;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 120ms ease;
}
.primary-btn:hover {
  background: #017a48;
}

/* Mobile */
@media (max-width: 991px) {
  .account-shell {
    flex-direction: column;
  }
  .settings-rail {
    width: 100%;
    position: static;
    height: auto;
    border-right: 0;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  }
  .rail-head {
    display: none;
  }
  .rail-scroll {
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    padding: 8px 12px;
  }
  .rail-nav-item {
    flex-shrink: 0;
    width: auto;
    white-space: nowrap;
  }
  .account-main-inner {
    padding: 16px;
  }
}
</style>
